import { useApiClient } from '@brewardapp/api-client';
import { Badge, Card, Loader } from '@breward/ui';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { sliderRangeText } from '../../../utils/intensityScale';
import { validate as validateUUID } from 'uuid';

import { Style, BeerGuidelines } from '../types';

interface EnhancedStyleGuidelinesViewProps {
    style: Style;
}

// Component for rendering individual descriptor badges
const DescriptorBadge = ({ value }: { value: string }) => {
    const api = useApiClient();
    const isUUID = validateUUID(value);

    const { data: descriptorData, isLoading } = api.descriptors.useFindDescriptor(
        {
            filter: {
                id: value,
            },
        },
        {
            enabled: isUUID,
        },
    );

    if (isLoading) {
        return (
            <Badge
                label={
                    <div className="flex items-center gap-1">
                        <div className="icon-[ph--spinner] h-3 w-3 animate-spin" />
                        <span>Loading...</span>
                    </div>
                }
                type="primary"
                className="text-xs"
            />
        );
    }

    const displayName = isUUID ? ((descriptorData as any)?.data?.name || 'Unknown descriptor') : value;

    return (
        <Badge
            label={displayName}
            type="primary"
            className="text-xs"
        />
    );
};

const EnhancedStyleGuidelinesView = ({ style }: EnhancedStyleGuidelinesViewProps) => {
    const [t, i18n] = useTranslation();
    const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
    const api = useApiClient();

    // Fetch the "Metryczka PSPD" scoresheet schema
    const { data: scoresheetSchemasData, isLoading: schemasLoading } =
        api.scoresheetSchemas.useListScoresheetSchemas({
            filter: {
                state: 'approved',
            },
        });

    // Find the Metryczka PSPD schema
    const metryczkaSchema = (scoresheetSchemasData as any)?.data?.find(
        (schema: any) => schema.name === 'Metryczka PSPD'
    );

    const { data: scoresheetSchemaData, isLoading: schemaLoading } =
        api.scoresheetSchemas.useFindScoresheetSchema(
            {
                filter: {
                    id: metryczkaSchema?.id,
                },
            },
            {
                enabled: !!metryczkaSchema?.id,
            },
        );

    const toggleSection = (sectionKey: string) => {
        setExpandedSections(prev => ({
            ...prev,
            [sectionKey]: !prev[sectionKey]
        }));
    };

    const renderValue = (min?: number | null, max?: number | null, unit: string = '') => {
        if (!min && !max) {
            return (
                <div className="flex items-center justify-center">
                    <div className="icon-[ph--minus-bold] h-4 w-4" />
                </div>
            );
        }
        return (
            <div className="font-medium">
                {min || '?'} - {max || '?'}{unit}
            </div>
        );
    };

    const renderParameterCard = (icon: string, label: string, value: React.ReactNode, color: string) => (
        <div
            className={`group relative overflow-hidden rounded-xl bg-gradient-to-br ${color} p-4 shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-105`}
        >
            <div className="relative z-10">
                <div className="flex items-center justify-between mb-2">
                    <div className={`${icon} h-6 w-6 text-white/90`} />
                    <div className="text-xs font-medium text-white/70 uppercase tracking-wider">
                        {label}
                    </div>
                </div>
                <div className="text-lg font-bold text-white">
                    {value}
                </div>
            </div>
            <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        </div>
    );

    const renderGuidelineRule = (rule: any, field: any) => {
        switch (rule?.ruleType) {
            case 'range': {
                const rangeText = sliderRangeText(
                    t,
                    field?.settings,
                    Number(rule?.settings?.minValue),
                    Number(rule?.settings?.maxValue),
                    i18n?.language,
                );
                return (
                    <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1">
                            {Array.from({ length: field?.settings?.maxValue - field?.settings?.minValue + 1 }, (_, i) => {
                                const value = field?.settings?.minValue + i;
                                const isInRange = value >= rule?.settings?.minValue && value <= rule?.settings?.maxValue;
                                return (
                                    <div
                                        key={value}
                                        className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold transition-all duration-200 ${isInRange
                                            ? 'bg-primary-500 text-white shadow-md'
                                            : 'bg-primary-100 text-primary-400 dark:bg-primary-800 dark:text-primary-600'
                                            }`}
                                    >
                                        {value}
                                    </div>
                                );
                            })}
                        </div>
                        <div className="text-sm text-primary-600 dark:text-primary-400">
                            ({rule?.settings?.minValue} - {rule?.settings?.maxValue})
                            {rangeText && <span className="ml-1 font-normal">{rangeText}</span>}
                        </div>
                    </div>
                );
            }

            case 'inclusion':
                return (
                    <div className="flex flex-col gap-2">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-1.5">
                            {rule?.settings?.values?.map((value: string, index: number) => (
                                <DescriptorBadge key={index} value={value} />
                            ))}
                        </div>
                    </div>
                );

            case 'boolean':
                return (
                    <div className="flex items-center gap-2">
                        <Badge
                            label={
                                <div className="flex items-center gap-1.5">
                                    <div className={`i-ph-${rule?.settings?.value ? 'check' : 'x'}-bold h-3 w-3`} />
                                    <span className="text-xs font-medium">
                                        {rule?.settings?.value ? t('global:shared.yes', 'Yes') : t('global:shared.no', 'No')}
                                    </span>
                                </div>
                            }
                            type={rule?.settings?.value ? "success" : "error"}
                            className="text-xs"
                        />
                    </div>
                );

            case 'balance':
                const ratios = rule?.settings?.ratios || [];
                const labels = field?.settings?.values || [];
                const total = ratios.reduce((sum: number, ratio: number) => sum + ratio, 0);

                // Define colors for different segments
                const colors = [
                    'bg-primary-500',
                    'bg-accent-200',
                    'bg-secondary-500'
                ];

                return (
                    <div className="space-y-3">
                        {/* Horizontal bar visualization */}
                        <div className="w-full">
                            <div className="flex h-6 rounded-lg overflow-hidden border border-primary-200 dark:border-primary-700 shadow-sm">
                                {ratios.map((ratio: number, index: number) => {
                                    const percentage = total > 0 ? (ratio / total) * 100 : 0;
                                    const colorClass = colors[index % colors.length];

                                    return (
                                        <div
                                            key={index}
                                            className={`${colorClass} flex items-center justify-center text-white text-xs font-medium transition-all duration-300 hover:brightness-110`}
                                            style={{ width: `${percentage}%` }}
                                            title={`${labels[index] || `Segment ${index + 1}`}: ${ratio} (${percentage.toFixed(1)}%)`}
                                        >
                                            {percentage > 15 && ratio}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Legend */}
                        <div className="flex flex-wrap gap-2">
                            {labels.map((label: string, index: number) => {
                                const ratio = ratios[index] || 0;
                                const percentage = total > 0 ? (ratio / total) * 100 : 0;
                                const colorClass = colors[index % colors.length];

                                return (
                                    <div key={index} className="flex items-center gap-1.5">
                                        <div className={`w-3 h-3 rounded-sm ${colorClass}`} />
                                        <span className="text-xs text-primary-600 dark:text-primary-400 font-medium">
                                            {label}: {ratio} ({percentage.toFixed(1)}%)
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );

            default:
                return (
                    <div className="text-sm text-primary-500 dark:text-primary-400">
                        {JSON.stringify(rule?.ruleType)}
                    </div>
                );
        }
    };

    const getSection = (key: string) => {
        return (scoresheetSchemaData as any)?.data?.sections?.find((section: any) => section?.key === key);
    };

    const getSubsection = (sectionKey: string, key: string) => {
        return getSection(sectionKey)?.subsections?.find(
            (subsection: any) => subsection?.key === key,
        );
    };

    const getField = (sectionKey: string, subsectionKey: string, key: string) => {
        return getSubsection(sectionKey, subsectionKey)?.fields?.find(
            (field: any) => field?.key === key,
        );
    };

    if (schemasLoading || schemaLoading) {
        return <Loader />;
    }

    return (
        <div className="space-y-6">
            {/* Basic Parameters Section */}
            <Card className="overflow-hidden">
                <div className="p-6 col-span-12 font-sans">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-500 text-white">
                            <div className="icon-[ph--chart-bar] h-5 w-5" />
                        </div>
                        <div>
                            <h3 className="text-xl font-semibold text-primary-900 dark:text-primary-100">
                                {t('Pages:ParticipantWeb.ShowStylePage.Guidelines.label', 'Guidelines')}
                            </h3>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 2xl:grid-cols-5 gap-4">
                        {renderParameterCard(
                            'icon-[ph--flask]',
                            t('Pages:ParticipantWeb.ShowStylePage.Guidelines.extract', 'Extract'),
                            renderValue(style?.guidelines?.minOriginalExtract, style?.guidelines?.maxOriginalExtract, '°Blg'),
                            'from-blue-500 to-blue-600'
                        )}
                        {renderParameterCard(
                            'icon-[ph--drop]',
                            t('Pages:ParticipantWeb.ShowStylePage.Guidelines.extract', 'Extract'),
                            renderValue(style?.guidelines?.minFinalExtract, style?.guidelines?.maxFinalExtract, '°Blg'),
                            'from-cyan-500 to-cyan-600'
                        )}
                        {renderParameterCard(
                            'icon-[ph--leaf]',
                            t('Pages:ParticipantWeb.ShowStylePage.Guidelines.ibu', 'Bitterness'),
                            renderValue(style?.guidelines?.minIbu, style?.guidelines?.maxIbu, ' IBU'),
                            'from-green-500 to-green-600'
                        )}
                        {renderParameterCard(
                            'icon-[ph--palette]',
                            t('Pages:ParticipantWeb.ShowStylePage.Guidelines.ebc', 'Color'),
                            renderValue(style?.guidelines?.minColor, style?.guidelines?.maxColor, ' EBC'),
                            'from-amber-500 to-amber-600'
                        )}
                        {renderParameterCard(
                            'icon-[ph--percent]',
                            t('Pages:ParticipantWeb.ShowStylePage.Guidelines.abv', 'Alcohol'),
                            renderValue(style?.guidelines?.minAbv, style?.guidelines?.maxAbv, '%'),
                            'from-purple-500 to-purple-600'
                        )}
                    </div>
                </div>
            </Card>

            {/* Detailed Guidelines Section */}
            {style?.guidelines?.sections && (scoresheetSchemaData as any)?.data && (
                <Card className="overflow-hidden">
                    <div className="p-6 col-span-12 font-sans">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-500 text-white">
                                <div className="icon-[ph--list-checks] h-5 w-5" />
                            </div>
                            <div>
                                <h3 className="text-xl font-semibold text-primary-900 dark:text-primary-100">
                                    {t('Pages:ParticipantWeb.ShowStylePage.DetailedGuidelines.label', 'Detailed Guidelines')}
                                </h3>
                            </div>
                        </div>

                        <div className="space-y-4">
                            {style?.guidelines?.sections?.sort((a: any, b: any) => (getSection(a?.key)?.order ?? 0) - (getSection(b?.key)?.order ?? 0)).map((section: any) => {
                                const schemaSection = getSection(section?.key);
                                const isExpanded = expandedSections[section?.key];

                                return (
                                    <div key={section?.key} className="border border-primary-200 dark:border-primary-700 rounded-lg overflow-hidden">
                                        <div
                                            className="cursor-pointer bg-primary-50 dark:bg-primary-900/20 p-4 hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors duration-200"
                                            onClick={() => toggleSection(section?.key)}
                                        >
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-500 text-white text-sm font-semibold">
                                                        {(schemaSection?.order ?? -1) + 1 || '?'}
                                                    </div>
                                                    <div>
                                                        <h4 className="text-lg font-semibold text-primary-900 dark:text-primary-100">
                                                            {schemaSection?.label || section?.key}
                                                        </h4>
                                                    </div>
                                                </div>
                                                <div className={`icon-[ph--caret]-${isExpanded ? 'up' : 'down'} h-5 w-5 text-primary-500 transition-transform duration-200`} />
                                            </div>
                                        </div>

                                        <>
                                            {isExpanded && (
                                                <div
                                                    className="transition-all duration-300"
                                                >
                                                    <div className="p-4 bg-white dark:bg-content-800 space-y-4">
                                                        {section?.subsections?.map((subsection: any) => {
                                                            const schemaSubsection = getSubsection(section?.key, subsection?.key);

                                                            return (
                                                                <div key={subsection?.key} className="border-l-4 border-primary-300 pl-4">
                                                                    <h5 className="text-xl font-bold text-accent-500 dark:text-accent-400 mb-3 border-b border-b-solid border-accent-500 dark:border-accent-400 pb-2">
                                                                        {schemaSubsection?.label || subsection?.key}
                                                                    </h5>
                                                                    <div className="space-y-3">
                                                                        {subsection?.rules?.map((rule: any) => {
                                                                            const field = getField(section?.key, subsection?.key, rule?.key);

                                                                            return (
                                                                                <div key={rule?.key} className="bg-primary-25 dark:bg-primary-900/10 rounded-lg p-3">
                                                                                    <div className="flex-1">
                                                                                        <div className="text-sm font-medium text-primary-700 dark:text-primary-300 mb-2">
                                                                                            {field?.label || rule?.key}
                                                                                        </div>
                                                                                    </div>
                                                                                    <div className="flex-shrink-0">
                                                                                        {renderGuidelineRule(rule, field)}
                                                                                    </div>
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </Card>
            )}
        </div>
    );
};

export default EnhancedStyleGuidelinesView; 